import React, { useContext, useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { Formik, Form } from "formik";

import AddressForm from "./Forms/AddressForm";
import PaymentForm from "./Forms/PaymentForm";
import ReviewOrder from "./ReviewOrder";
import CheckoutSuccess from "./CheckoutSuccess";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

import * as Yup from "yup";
import validationSchema from "./FormModel/validationSchema";
import checkoutFormModel from "./FormModel/checkoutFormModel";
import formInitialValues from "./FormModel/formInitialValues";

import useStyles from "./styles";

export default function CheckoutPage(props) {
  const steps = ["Dados", "Personalizar", "Revisar"];
  const { formId, formField } = checkoutFormModel;

  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(1);
  const [datePayment, setDatePayment] = useState(null);
  const [invoiceId] = useState(props.Invoice.id);
  const [paymentText, setPaymentText] = useState("");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const currentValidationSchema = validationSchema[activeStep] || Yup.object().shape({});
  const isLastStep = activeStep === steps.length - 1;
  const { user } = useContext(AuthContext);

  function _renderStepContent(step, setFieldValue, setActiveStep, values) {
    switch (step) {
      case 0:
        return (
          <AddressForm
            formField={formField}
            values={values}
            setFieldValue={setFieldValue}
          />
        );
      case 1:
        return (
          <PaymentForm
            formField={formField}
            setFieldValue={setFieldValue}
            setActiveStep={setActiveStep}
            activeStep={step}
            invoiceId={invoiceId}
            values={values}
          />
        );
      case 2:
        return <ReviewOrder />;
      default:
        return <div>Not Found</div>;
    }
  }

  async function _payWithMethod(values, method) {
    setLoadingPayment(true);
    try {
      const plan = JSON.parse(values.plan);
      const invoiceValue = props.Invoice?.value || plan.price;
      const newValues = {
        firstName: values.firstName,
        lastName: values.lastName,
        address2: values.address2,
        city: values.city,
        state: values.state,
        zipcode: values.zipcode,
        country: values.country,
        useAddressForPaymentDetails: values.useAddressForPaymentDetails,
        nameOnCard: values.nameOnCard,
        cardNumber: values.cardNumber,
        cvv: values.cvv,
        plan: values.plan,
        price: invoiceValue,
        users: plan.users,
        connections: plan.connections,
        invoiceId: invoiceId,
        method: method,
      };

      const { data } = await api.post("/subscription", newValues);
      setDatePayment(data);

      if (method === "mercadopago" && data.urlMcPg) {
        setPaymentText("Ao realizar o pagamento, atualize a pagina!");
        window.open(data.urlMcPg, "_blank");
        toast.success(
          "Assinatura realizada com sucesso! Aguardando o pagamento."
        );
      } else if (method === "cartao" && data.paymentLinkUrl) {
        setPaymentText("Redirecionando para o pagamento seguro...");
        window.open(data.paymentLinkUrl, "_blank");
        toast.success("Redirecionando para o checkout seguro do Asaas.");
      } else {
        setPaymentText("");
        setActiveStep(activeStep + 1);
        toast.success("Pagamento iniciado! Confira os dados abaixo.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingPayment(false);
    }
  }

  function _handleSubmit(values, actions) {
    if (isLastStep) {
      // No step 2 (Revisar) o submit nao faz nada automatico;
      // os botoes de metodo de pagamento sao renderizados manualmente abaixo
      actions.setSubmitting(false);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }

  function _handleBack() {
    setActiveStep(activeStep - 1);
  }

  return (
    <React.Fragment>
      <Typography component="h1" variant="h4" align="center">
        Falta pouco!
      </Typography>
      <Stepper activeStep={activeStep} className={classes.stepper}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <React.Fragment>
        {activeStep === steps.length ? (
          <CheckoutSuccess pix={datePayment} />
        ) : (
          <Formik
            initialValues={{
              ...user,
              ...formInitialValues,
            }}
            validationSchema={currentValidationSchema}
            onSubmit={_handleSubmit}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form id={formId}>
                {_renderStepContent(
                  activeStep,
                  setFieldValue,
                  setActiveStep,
                  values
                )}

                <div className={classes.buttons}>
                  {activeStep !== 1 && (
                    <Button onClick={_handleBack} className={classes.button}>
                      VOLTAR
                    </Button>
                  )}

                  {/* Botoes de pagamento no step 2 (Revisar) */}
                  {activeStep === 2 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        width: "100%",
                        marginTop: 16,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        align="center"
                        style={{ fontWeight: 600, marginBottom: 8 }}
                      >
                        Escolha como deseja pagar
                      </Typography>

                      <Button
                        disabled={loadingPayment}
                        onClick={() => _payWithMethod(values, "pix")}
                        variant="contained"
                        startIcon={
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="24" height="24" rx="4" fill="white" fillOpacity="0.2"/>
                            <path d="M7 12h2v2H7v-2zm4-2h2v4h-2v-4zm4-2h2v6h-2V8zm-8 8h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z" fill="white"/>
                          </svg>
                        }
                        style={{
                          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                          color: "#fff",
                          padding: "14px 24px",
                          fontWeight: 700,
                          fontSize: "1rem",
                          borderRadius: "12px",
                          boxShadow: "0 4px 14px rgba(17, 153, 142, 0.4)",
                          textTransform: "none",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {loadingPayment ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          "Pagar com PIX"
                        )}
                      </Button>

                          <Button
                        disabled={loadingPayment}
                        onClick={() => _payWithMethod(values, "cartao")}
                        variant="contained"
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "#fff",
                          padding: "14px 24px",
                          fontWeight: 700,
                          fontSize: "1rem",
                          borderRadius: "12px",
                          boxShadow: "0 4px 14px rgba(102, 126, 234, 0.4)",
                          textTransform: "none",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {loadingPayment ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          "Pagar com Cartão de Crédito"
                        )}
                      </Button>

                    </div>
                  )}

                  {/* Botao padrao para steps 0 e 1 */}
                  {activeStep !== 2 && (
                    <div className={classes.wrapper}>
                      <Button
                        disabled={isSubmitting}
                        type="submit"
                        variant="contained"
                        color="primary"
                        className={classes.button}
                      >
                        {isLastStep ? "PAGAR" : "PROXIMO"}
                      </Button>
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </div>
                  )}
                </div>

                {paymentText && (
                  <div
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "10px",
                      borderRadius: "8px",
                      marginTop: "10px",
                    }}
                  >
                    <Typography
                      variant="h5"
                      align="center"
                      style={{
                        color: "#ff5722",
                        fontWeight: "bold",
                        fontFamily: "cursive",
                      }}
                    >
                      {paymentText}
                    </Typography>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        )}
      </React.Fragment>
    </React.Fragment>
  );
}
